import { GRUPO_USUARIOS_QUERY } from '../data/grupo-usuarios'
import { GRUPO_USUARIOS_DOMINIO_QUERY } from '../data/grupo-usuarios-dominio'
import {
  GetGrupoUsuariosDominioQueryQuery,
  GetGrupoUsuariosDominioQueryQueryVariables,
  GetGrupoUsuariosQueryQuery,
  GetGrupoUsuariosQueryQueryVariables,
  GrupoUsuarios,
  GrupoUsuariosInput,
  SalvarGrupoUsuariosMutationMutation,
  SalvarGrupoUsuariosMutationMutationVariables
} from '@/core/types/graphql'
import { SALVAR_GRUPO_USUARIOS } from '../data/salvar-grupo-usuarios'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useCallback } from 'react'
import { useConfirmModal } from '@/core/context/confirm-modal-context'
import { t } from '@lingui/core/macro'
import { Routes } from '@/core/router/routes'
import { UseFormReset } from 'react-hook-form'
import { GrupoUsuariosValues } from './use-form'
import { request } from '../../../core/utils/request'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

export const useGrupoUsuariosEdicaoQueryHandler = () => {
  const client = useQueryClient()
  const { id } = useParams()
  const { pathname } = useLocation()
  const { askUser } = useConfirmModal()
  const navigate = useNavigate()

  const getGrupoUsuariosDominioQuery = useQuery({
    queryKey: ['grupoUsuariosDominio'],
    queryFn: async () =>
      await request<
        GetGrupoUsuariosDominioQueryQuery,
        GetGrupoUsuariosDominioQueryQueryVariables
      >(GRUPO_USUARIOS_DOMINIO_QUERY)
  })

  const getGrupoUsuariosQuery = useQuery({
    queryKey: ['grupoUsuarios', id],
    queryFn: async () =>
      await request<
        GetGrupoUsuariosQueryQuery,
        GetGrupoUsuariosQueryQueryVariables
      >(GRUPO_USUARIOS_QUERY, { variables: { id: id! } }),
    enabled: true,
    staleTime: 0
  })
  const salvarGrupoUsuariosMutation = useMutation({
    mutationKey: ['salvarGrupoUsuarios'],
    mutationFn: async ({
      variables
    }: {
      variables: SalvarGrupoUsuariosMutationMutationVariables
    }) =>
      await request<
        SalvarGrupoUsuariosMutationMutation,
        SalvarGrupoUsuariosMutationMutationVariables
      >(SALVAR_GRUPO_USUARIOS, { variables }),
    onSuccess: async () => {
      await client.refetchQueries({ queryKey: ['gruposUsuariosPaged'] })
      await client.refetchQueries({ queryKey: ['grupoUsuarios'] })
      await client.refetchQueries({ queryKey: ['gruposUsuariosAtivos'] })
    }
  })

  const salvarGrupoUsuarios = useCallback(
    ({
      grupoUsuariosInput,
      resetForm,
      onOpen,
      onAskUserConfirm
    }: {
      grupoUsuariosInput: GrupoUsuariosInput
      resetForm: UseFormReset<GrupoUsuariosValues>
      onOpen?: () => void
      onAskUserConfirm?: (grupo: GrupoUsuarios) => void
    }) => {
      askUser({
        onConfirm: async () => {
          const result = await salvarGrupoUsuariosMutation.mutateAsync({
            variables: { grupoUsuarios: grupoUsuariosInput }
          })
          if (pathname.includes('/gruposUsuarios')) {
            navigate(Routes.GRUPOS_USUARIOS)
          } else {
            onAskUserConfirm?.(result.salvarGrupoUsuarios as GrupoUsuarios)
          }
          resetForm()
        },
        onCancel: () => onOpen?.(),
        title: t`Deseja salvar o grupo de usuários?`,
        confirmLabel: t`Salvar`,
        successMessage: t`Grupo de Usuários ${grupoUsuariosInput.nome} salvo com sucesso!`,
        errorMessage: t`Não foi possível salvar Grupo de Usuarios`
      })
    },
    [salvarGrupoUsuariosMutation, askUser]
  )

  return {
    getGrupoUsuariosDominioQuery,
    getGrupoUsuariosQuery,
    salvarGrupoUsuarios
  }
}
